/**
 * Education Reward Detail Component
 * Displays education reward information with conditional distributor field
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import moment from 'moment';
import sanitizeHtml from 'sanitize-html';
import Layout from '../../../../../components/Layout';
import Link from '../../../../../components/Link';
import { sidebar, sanitizeHtmlOptions } from '../../../../../configPublic';
import MiniNavSimple from '../../../../../components/MiniNavSimple';

const messages = defineMessages({
  points: {
    id: 'educationReward.detail.points',
    defaultMessage: 'Points',
    description: 'Points',
  },
  locationName: {
    id: 'educationReward.detail.locationName',
    defaultMessage: 'Location',
    description: 'Location',
  },
  distributor: {
    id: 'educationReward.detail.distributor',
    defaultMessage: 'Distributor',
    description: 'Distributor',
  },
  province: {
    id: 'educationReward.detail.province',
    defaultMessage: 'Province',
    description: 'Province',
  },
  city: {
    id: 'educationReward.detail.city',
    defaultMessage: 'City',
    description: 'City',
  },
  dateTime: {
    id: 'educationReward.detail.dateTime',
    defaultMessage: 'Event Date & Time',
    description: 'Event Date & Time',
  },
  inventoryLimit: {
    id: 'educationReward.detail.inventoryLimit',
    defaultMessage: 'Available Spots',
    description: 'Available Spots',
  },
  redemptionLimit: {
    id: 'educationReward.detail.redemptionLimit',
    defaultMessage: 'Redemption Limit',
    description: 'Redemption Limit',
  },
  subCategory: {
    id: 'educationReward.detail.subCategory',
    defaultMessage: 'Category',
    description: 'Category',
  },
  tags: {
    id: 'educationReward.detail.tags',
    defaultMessage: 'Tags',
    description: 'Tags',
  },
});

class EducationRewardDetail extends Component {
  static propTypes = {
    item: PropTypes.object.isRequired,
    siteId: PropTypes.string.isRequired,
    sidebarKey: PropTypes.string.isRequired,
    paths: PropTypes.array,
  };

  static contextTypes = {
    routeParams: PropTypes.object.isRequired,
    url: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
    path: PropTypes.string.isRequired,
  };

  render() {
    const { item, siteId } = this.props;
    const { intl } = this.context;
    const data = item.data || {};

    const tags = (item.tags || []).map((tag, index) => {
      const tagUrl = `/${siteId}/education-rewards?filters[0][key]=tags&filters[0][operator]=strIs&filters[0][value]=${tag}&filters[0][label]=${tag}`;
      const tagComponent = (
        <Link className="product-detail__tags__tag" to={tagUrl}>
          {tag}
        </Link>
      );
      return index === item.tags.length - 1 ? (
        <span key={tag}>{tagComponent}</span>
      ) : (
        <span key={tag}>{tagComponent}, </span>
      );
    });

    const images = (item.gallery || []).map(image => (
      <div key={image.media._id} className="education-reward-detail__image">
        <img
          alt={item.n}
          src={`/${siteId}/asset/${image.media._id}?type=fill&width=800&height=600`}
          style={{ width: '100%', height: 'auto', objectFit: 'cover' }}
        />
      </div>
    ));

    const points =
      item.ecommerce &&
      item.ecommerce.price &&
      item.ecommerce.price.currency === 'POINTS'
        ? item.ecommerce.price.amount
        : null;

    return (
      <Layout sidebar={sidebar[this.props.sidebarKey]}>
        {this.props.paths && <MiniNavSimple paths={this.props.paths} />}
        <div className="content__body">
          <section className="mod education-reward-detail-section">
            <div className="education-reward-detail">
              <div className="row">
                <div className="columns small-12 medium-6">
                  {images.length > 0 && (
                    <div className="education-reward-detail__images">
                      {images}
                    </div>
                  )}
                </div>
                <div className="columns small-12 medium-6">
                  <div className="education-reward-detail__info">
                    <h1 className="education-reward-detail__title">
                      {item.n}
                    </h1>

                    {data.subCategory && (
                      <div className="education-reward-detail__category">
                        <strong>
                          <FormattedMessage {...messages.subCategory} />:{' '}
                        </strong>
                        {data.subCategory}
                      </div>
                    )}

                    {points != null && (
                      <div className="education-reward-detail__points">
                        <strong>
                          <FormattedMessage {...messages.points} />:{' '}
                        </strong>
                        {points}
                      </div>
                    )}

                    {item.desc && (
                      <div
                        className="education-reward-detail__description"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(item.desc, sanitizeHtmlOptions),
                        }}
                      />
                    )}

                    <div className="education-reward-detail__event-info">
                      <h3>Event Information</h3>

                      {data.dateTime && (
                        <div className="education-reward-detail__datetime">
                          <strong>
                            <FormattedMessage {...messages.dateTime} />:{' '}
                          </strong>
                          {moment(data.dateTime).format(
                            'MMMM D, YYYY [at] h:mm A',
                          )}
                        </div>
                      )}

                      {data.locationName && (
                        <div className="education-reward-detail__location">
                          <strong>
                            <FormattedMessage {...messages.locationName} />:{' '}
                          </strong>
                          {data.locationName}
                        </div>
                      )}

                      {data.city && (
                        <div className="education-reward-detail__city">
                          <strong>
                            <FormattedMessage {...messages.city} />:{' '}
                          </strong>
                          {data.city}
                        </div>
                      )}

                      {data.province && (
                        <div className="education-reward-detail__province">
                          <strong>
                            <FormattedMessage {...messages.province} />:{' '}
                          </strong>
                          {data.province}
                        </div>
                      )}

                      {/* Conditional display: only show distributor if it has a value */}
                      {data.distributor && data.distributor.trim() !== '' && (
                        <div className="education-reward-detail__distributor">
                          <strong>
                            <FormattedMessage {...messages.distributor} />:{' '}
                          </strong>
                          {data.distributor}
                        </div>
                      )}

                      {data.inventoryLimit != null && (
                        <div className="education-reward-detail__inventory">
                          <strong>
                            <FormattedMessage {...messages.inventoryLimit} />:{' '}
                          </strong>
                          {data.inventoryLimit}
                        </div>
                      )}

                      {data.redemptionLimit != null && (
                        <div className="education-reward-detail__redemption">
                          <strong>
                            <FormattedMessage {...messages.redemptionLimit} />:{' '}
                          </strong>
                          {data.redemptionLimit}
                        </div>
                      )}
                    </div>

                    {tags.length > 0 && (
                      <div className="education-reward-detail__tags">
                        <strong>
                          <FormattedMessage {...messages.tags} />:{' '}
                        </strong>
                        {tags}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </Layout>
    );
  }
}

export default EducationRewardDetail;



// WEBPACK FOOTER //
// ./src/routes/client/site/education-rewards/detail/EducationRewardDetail.js